import type { InventoryDetail } from '@epc/contracts';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { toast } from '../../ui/feedback';
import { Input } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { inventoriesQuery, inventoryQuery, parseNum, qty } from './queries';

/** Пересчёт с телефона по QR (СК-26): на карточке позиции — поле ввода для каждой идущей инвентаризации */
export function QuickCount({ itemId }: { itemId: string }) {
  const qc = useQueryClient();
  const open = useQuery(inventoriesQuery({ status: 'counting' }));
  const details = useQueries({ queries: (open.data ?? []).map((i) => inventoryQuery(i.id)) });
  const [vals, setVals] = useState<Record<string, string>>({});
  const save = useMutation({
    mutationFn: (v: { inventoryId: string; lineId: string; value: string }) =>
      api<InventoryDetail>(`/warehouse/inventories/${v.inventoryId}/count`, {
        body: { lines: [{ lineId: v.lineId, countedQty: v.value.trim() === '' ? null : parseNum(v.value) }] },
      }),
    onSuccess: (d) => {
      qc.setQueryData(inventoryQuery(d.id).queryKey, d);
      qc.invalidateQueries({ queryKey: ['wh', 'inventories'] });
      toast('Пересчёт записан');
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const add = useMutation({
    mutationFn: (inventoryId: string) =>
      api<InventoryDetail>(`/warehouse/inventories/${inventoryId}/lines`, { body: { itemId } }),
    onSuccess: (d) => qc.setQueryData(inventoryQuery(d.id).queryKey, d),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const rows = details.map((q) => q.data).filter((d): d is InventoryDetail => Boolean(d?.can.count));
  if (!rows.length) return null;
  return (
    <Panel title="Идёт инвентаризация — пересчёт" meta="пустое поле — не проверено">
      <ul className="flex flex-col gap-3">
        {rows.map((d) => {
          const line = d.lineList.find((l) => l.itemId === itemId);
          const key = `${d.id}`;
          return (
            <li key={d.id} className="flex flex-col gap-1.5">
              <Link to={`/warehouse/inventories/${d.id}`} className="text-[14px] font-medium hover:underline">
                {d.number} · {d.warehouseName}
              </Link>
              {line ? (
                <>
                  <span className="num text-[13px] text-ink-2">
                    ожидаемое {qty(line.expectedQty)} {line.unit}
                    {line.countedQty !== null ? ` · пересчитано ${qty(line.countedQty)}` : ''}
                  </span>
                  <div className="flex gap-2">
                    <Input
                      inputMode="decimal"
                      className="max-w-[160px]"
                      placeholder="Пересчитано"
                      value={vals[key] ?? (line.countedQty === null ? '' : String(line.countedQty))}
                      onChange={(e) => setVals({ ...vals, [key]: e.target.value })}
                    />
                    <Button
                      variant="primary"
                      loading={save.isPending && save.variables?.inventoryId === d.id}
                      onClick={() =>
                        save.mutate({
                          inventoryId: d.id,
                          lineId: line.id,
                          value: vals[key] ?? (line.countedQty === null ? '' : String(line.countedQty)),
                        })
                      }
                    >
                      Записать
                    </Button>
                  </div>
                </>
              ) : (
                <div>
                  <span className="text-[13px] text-ink-2">По учёту этой позиции на складе нет. </span>
                  <Button size="sm" loading={add.isPending} onClick={() => add.mutate(d.id)}>
                    Нашлась — добавить в ведомость
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </Panel>
  );
}
